import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldAlert, Gavel, Scale, Flag, FileWarning, RefreshCw } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Loader, Empty, Card } from "../components/UI";

const STATUS_TONE = {
  CLEARED: "text-emerald-300 border-emerald-500/30",
  SANCTION_ACTIVE: "text-red-300 border-red-500/30",
  REFERRED_TO_DISCIPLINARY: "text-amber-300 border-amber-500/30",
  UNDER_REVIEW: "text-[#F0C72A] border-[#E5B31E]/30",
  OPEN: "text-slate-300 border-slate-600/40",
};

export default function IntegrityPage() {
  const { hasRole } = useAuth();
  const [tab, setTab] = useState("flags");
  const [config, setConfig] = useState(null);
  const [flags, setFlags] = useState([]);
  const [cases, setCases] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const canIntegrity = hasRole("WSBF_INTEGRITY_OFFICER", "WSBF_ADMIN");
  const canDisc = hasRole("WSBF_DISCIPLINARY_OFFICER", "WSBF_ADMIN");
  const canApp = hasRole("WSBF_APPEALS_OFFICER", "WSBF_ADMIN");

  const load = useCallback(() => {
    api.get("/integrity/config").then(({ data }) => setConfig(data)).catch(() => {});
    if (canIntegrity) api.get("/integrity/flags").then(({ data }) => setFlags(data.flags)).catch(() => {});
    api.get("/integrity/cases").then(({ data }) => setCases(data.cases)).catch(() => {});
    if (canApp) api.get("/integrity/appeals").then(({ data }) => setAppeals(data.appeals)).catch(() => {});
  }, [canIntegrity, canApp]);
  useEffect(() => { load(); }, [load]);

  const openCase = async (cid) => {
    try { const { data } = await api.get(`/integrity/cases/${cid}`); setSel(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); load(); if (sel) openCase(sel.id); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!config) return <Loader label="Loading integrity module…" />;

  const TABS = [
    { k: "flags", label: "Flags Queue", Icon: Flag, show: canIntegrity },
    { k: "cases", label: "Cases", Icon: ShieldAlert, show: true },
    { k: "disciplinary", label: "Disciplinary", Icon: Gavel, show: canDisc },
    { k: "appeals", label: "Appeals", Icon: Scale, show: canApp },
  ].filter((t) => t.show);

  return (
    <Page>
      <PageTitle eyebrow="WSTF Master Rulebook · Book V" title="Integrity & Disciplinary"
        subtitle="Flags are investigative signals — never guilt. Sanctions are reasoned human decisions; appeals are heard by a different officer (Art. 369)."
        right={canIntegrity ? (
          <button data-testid="run-signals" disabled={busy}
            onClick={() => act(async () => { const { data } = await api.post("/integrity/signals/run"); toast.success(`${data.created} flags created`); })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[#2b2512] text-slate-200 hover:border-[#E5B842]/50 disabled:opacity-40">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Run Signals
          </button>
        ) : null} />

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="integrity-tabs">
        {TABS.map(({ k, label, Icon }) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide border ${tab === k ? "bg-[#E5B842] text-[#000000] border-[#E5B842]" : "bg-[#161616] text-slate-300 border-[#2b2512]"}`}
            style={{ transition: "background-color 0.15s ease" }}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {tab === "flags" && (flags.length === 0 ? <Empty label="No flags." /> : flags.map((f) => (
            <Card key={f.id} className="p-4" data-testid={`flag-${f.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-fuchsia-300">{f.signal_type}</span>
                <span className="text-[10px] font-mono text-slate-500">{f.severity_hint}{f.is_demo ? " · DEMO" : ""}</span>
              </div>
              <div className="text-sm text-slate-300 mt-1">{f.evidence?.detail}</div>
              {canIntegrity && f.status === "PENDING_REVIEW" && (
                <button data-testid={`open-flag-${f.id}`} disabled={busy}
                  onClick={() => act(async () => { const { data } = await api.post(`/integrity/flags/${f.id}/open`); setSel(data); toast.success("Case opened"); })}
                  className="mt-3 text-xs font-bold uppercase px-3 py-1.5 rounded bg-[#161616] border border-[#2b2512] text-[#E5B842]">
                  Open Case →
                </button>
              )}
              {f.status !== "PENDING_REVIEW" && <div className="mt-2 text-[10px] font-mono text-slate-500">{f.status}</div>}
            </Card>
          )))}

          {(tab === "cases" || tab === "disciplinary") && (
            (tab === "disciplinary" ? cases.filter((c) => ["REFERRED_TO_DISCIPLINARY", "RESPONDENT_RESPONSE"].includes(c.status)) : cases)
              .map((c) => (
                <button key={c.id} data-testid={`case-${c.id}`} onClick={() => openCase(c.id)}
                  className="w-full text-left"><Card className="p-4 hover:border-[#E5B842]/40" >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-heading font-bold text-slate-100">{c.case_no}</span>
                    <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${STATUS_TONE[c.status] || "text-slate-400 border-slate-600/40"}`}>{c.status}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{c.signal_type} · {c.entity?.name}{c.is_demo ? " · DEMO" : ""}</div>
                </Card></button>
              ))
          )}

          {tab === "appeals" && (appeals.length === 0 ? <Empty label="No appeals." /> : appeals.map((a) => (
            <Card key={a.id} className="p-4" data-testid={`appeal-${a.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-heading font-bold text-slate-100">{a.appeal_no}</span>
                <span className="text-[9px] font-mono uppercase text-amber-300">{a.status}{a.urgent ? " · URGENT" : ""}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">{(a.grounds || []).join(", ")}</div>
              {canApp && a.status === "FILED" && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <AppealBtn label="Affirm" onClick={() => act(async () => { await api.post(`/integrity/appeals/${a.id}/decide`, { outcome: "AFFIRM", reasoned_text: "Upheld on review." }); toast.success("Affirmed"); })} />
                  <AppealBtn label="Modify→L1" onClick={() => act(async () => { await api.post(`/integrity/appeals/${a.id}/decide`, { outcome: "MODIFY", new_level: "L1", reasoned_text: "Reduced to a warning." }); toast.success("Modified"); })} />
                  <AppealBtn label="Overturn" onClick={() => act(async () => { await api.post(`/integrity/appeals/${a.id}/decide`, { outcome: "OVERTURN", reasoned_text: "Overturned; evidence insufficient." }); toast.success("Overturned"); })} />
                  <AppealBtn label={a.stay_granted ? "Stayed" : "Grant stay"} onClick={() => act(async () => { await api.post(`/integrity/appeals/${a.id}/stay`, { grant: true }); toast.success("Stay granted"); })} />
                </div>
              )}
              {a.outcome && <div className="mt-2 text-[10px] font-mono text-emerald-300">Outcome: {a.outcome}</div>}
            </Card>
          )))}
        </div>

        <div className="lg:col-span-3">
          {sel ? <CaseDetail c={sel} config={config} canIntegrity={canIntegrity} canDisc={canDisc}
            busy={busy} act={act} api={api} /> : <Card className="p-8 text-center text-slate-500" data-testid="case-detail-empty">Select a case to view its file.</Card>}
        </div>
      </div>
    </Page>
  );
}

function AppealBtn({ label, onClick }) {
  return <button data-testid={`appeal-action-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} onClick={onClick}
    className="text-[11px] font-bold uppercase px-2.5 py-1 rounded bg-[#161616] border border-[#2b2512] text-slate-200 hover:border-[#E5B842]/50">{label}</button>;
}

function CaseDetail({ c, config, canIntegrity, canDisc, busy, act }) {
  const [note, setNote] = useState("");
  const [codes, setCodes] = useState([]);
  const [level, setLevel] = useState("L2");
  const [reason, setReason] = useState("");
  const referable = ["OPEN", "UNDER_REVIEW"].includes(c.status);
  const decidable = ["REFERRED_TO_DISCIPLINARY", "RESPONDENT_RESPONSE"].includes(c.status);

  return (
    <Card className="p-6" data-testid="case-detail">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-black text-slate-50">{c.case_no}</h2>
        <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded border ${STATUS_TONE[c.status] || "text-slate-400 border-slate-600/40"}`}>{c.status}</span>
      </div>
      <div className="text-sm text-slate-400 mt-1">{c.signal_type} · Respondent: {c.entity?.name}</div>
      <div className="mt-2 text-xs font-mono text-slate-500 space-y-0.5">
        <div>Integrity officer: {c.integrity_officer?.name || "—"}</div>
        <div>Referred by: {c.referred_by?.name || "—"}</div>
        <div>Disciplinary officer: {c.disciplinary_officer?.name || "—"}</div>
      </div>

      {c.violation_codes?.length > 0 && <div className="mt-3 text-xs text-amber-300 font-mono">{c.violation_codes.join(", ")}</div>}
      {c.respondent_statement && <div className="mt-3 text-sm text-slate-300 bg-[#000000] border border-[#2b2512] rounded p-3">Respondent: “{c.respondent_statement.text}”</div>}
      {c.decision && (
        <div className="mt-3 rounded p-3 bg-[#1a1407] border border-[#E5B842]/30" data-testid="case-decision">
          <div className="text-[10px] font-mono uppercase text-[#E5B842]">Decision · {c.decision.level}</div>
          <div className="text-sm text-slate-200 mt-1">{c.decision.reasoned_text}</div>
          <div className="text-[10px] font-mono text-slate-500 mt-1">factors: {(c.decision.factors || []).join(", ")} · appeal by {new Date(c.decision.appeal_deadline).toLocaleDateString()}</div>
        </div>
      )}
      {c.sanction && (
        <div className="mt-2 text-xs font-mono" data-testid="case-sanction">
          Sanction: <span className={c.sanction.provisional ? "text-amber-300" : "text-red-300"}>{c.sanction.provisional ? "PROVISIONAL (pending process)" : c.sanction.type}</span> · {c.sanction.status} · scope {c.sanction.scope_type} {(c.sanction.scope_values || []).join(",")}
        </div>
      )}

      {canIntegrity && referable && (
        <div className="mt-5 border-t border-[#2b2512] pt-4 space-y-2" data-testid="investigate-panel">
          <textarea data-testid="invest-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Investigation note / evidence"
            className="w-full text-sm bg-[#000000] border border-[#2b2512] rounded p-2 text-slate-200" rows={2} />
          <select data-testid="violation-select" multiple value={codes} onChange={(e) => setCodes([...e.target.selectedOptions].map((o) => o.value))}
            className="w-full text-xs bg-[#000000] border border-[#2b2512] rounded p-2 text-slate-200 h-24">
            {config.violations.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="flex gap-2">
            <button data-testid="btn-investigate" disabled={busy} onClick={() => act(async () => { await api.post(`/integrity/cases/${c.id}/investigate`, { note }); })}
              className="text-xs font-bold uppercase px-3 py-1.5 rounded bg-[#161616] border border-[#2b2512] text-[#F0C72A]">Save Note</button>
            <button data-testid="btn-refer" disabled={busy} onClick={() => act(async () => { await api.post(`/integrity/cases/${c.id}/refer`, { violation_codes: codes, summary: note || "Referred for adjudication." }); })}
              className="text-xs font-bold uppercase px-3 py-1.5 rounded bg-[#161616] border border-amber-500/40 text-amber-300">Refer →</button>
            <button data-testid="btn-clear" disabled={busy} onClick={() => act(async () => { await api.post(`/integrity/cases/${c.id}/clear`, { reason: note || "No misconduct found." }); })}
              className="text-xs font-bold uppercase px-3 py-1.5 rounded bg-[#161616] border border-emerald-500/40 text-emerald-300">Clear</button>
          </div>
        </div>
      )}

      {canDisc && decidable && (
        <div className="mt-5 border-t border-[#2b2512] pt-4 space-y-2" data-testid="decide-panel">
          <div className="flex gap-2 items-center">
            <select data-testid="level-select" value={level} onChange={(e) => setLevel(e.target.value)}
              className="text-xs bg-[#000000] border border-[#2b2512] rounded p-2 text-slate-200">
              {Object.entries(config.sanction_levels).map(([k, v]) => <option key={k} value={k}>{k} — {v.label}</option>)}
            </select>
            <span className="text-[10px] font-mono text-slate-500">scope: global</span>
          </div>
          <textarea data-testid="decide-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reasoned decision (required, Art. 353)"
            className="w-full text-sm bg-[#000000] border border-[#2b2512] rounded p-2 text-slate-200" rows={3} />
          <button data-testid="btn-decide" disabled={busy || !reason} onClick={() => act(async () => {
            await api.post(`/integrity/cases/${c.id}/decide`, { level, scope_type: "GLOBAL", scope_values: [], factors: ["SEVERITY", "INTENT"], reasoned_text: reason, duration_days: 30 });
            toast.success("Decision recorded");
          })} className="text-xs font-bold uppercase px-4 py-2 rounded bg-[#E5B842] text-[#000000] disabled:opacity-40">Issue Reasoned Decision</button>
        </div>
      )}

      {c.events && c.events.length > 0 && (
        <div className="mt-5 border-t border-[#2b2512] pt-4" data-testid="case-timeline">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Timeline</div>
          {c.events.map((e, i) => (
            <div key={i} className="flex gap-2 text-[11px] text-slate-400 py-0.5">
              <span className="font-mono text-slate-600">{new Date(e.at).toLocaleString([], { hour12: false })}</span>
              <span className="text-slate-200">{e.event}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
